import { nextTick, onBeforeUnmount, watch } from 'vue'

/*
 * The header popovers — the notification bell, the language list, the account
 * menu and the phone search sheet — and the keyboard contract they share.
 *
 * ONE OPEN AT A TIME
 * Each closed itself on an outside click, but the language and search triggers
 * stop their click from propagating, so the document handler that closes the
 * account menu never saw it: the language list and the account menu could sit
 * open on top of each other. Rather than teaching every component about every
 * other, whichever opens takes the single slot below and the previous holder is
 * closed.
 *
 * FOCUS
 *  - Opening moves focus into the panel: to `initialFocus()` when the component
 *    names a better target (the selected language, the search input), else to
 *    the first control in it. Two of these panels are teleported to the end of
 *    <body>, so a keyboard user who opened one used to be left on the trigger
 *    with the panel reachable only by tabbing through the whole page.
 *  - Shift+Tab from the first control closes and returns to the trigger.
 *  - Tab past the last control closes and carries on to whatever follows the
 *    trigger, as if the panel had been rendered inline right after it.
 *  - Escape stays with each component (each already listens for it); they hand
 *    focus back through the same `close({ restoreFocus: true })`.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

// `getClientRects()` rather than `offsetParent`: offsetParent is null for
// position:fixed elements even while they are on screen.
const isTabbable = (el) =>
  el.tabIndex >= 0 && !el.closest('[inert]') && el.getClientRects().length > 0

/** Every element the Tab key can reach inside `root`, in document order. */
export const tabbablesIn = (root) =>
  root ? Array.from(root.querySelectorAll(FOCUSABLE)).filter(isTabbable) : []

/** The first tabbable element after `from` in document order, skipping `skip`'s subtree. */
const nextTabbableAfter = (from, skip) => {
  if (!from) return null
  return tabbablesIn(document.body).find((el) =>
    !from.contains(el) &&
    !(skip && skip.contains(el)) &&
    (from.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)
  ) || null
}

let current = null
let counter = 0

/**
 * @param {Object} options
 * @param {string} options.name                      prefix for the panel id
 * @param {import('vue').Ref<boolean>} options.isOpen the component's own open state
 * @param {(opts: { restoreFocus: boolean }) => void} options.close
 * @param {import('vue').Ref<HTMLElement>} options.trigger
 * @param {import('vue').Ref<HTMLElement>} options.panel
 * @param {() => HTMLElement|null} [options.initialFocus]
 * @returns {{ panelId: string, onKeydown: (event: KeyboardEvent) => void }}
 *   `panelId` goes on the panel and in the trigger's `aria-controls`;
 *   `onKeydown` goes on the panel.
 */
export function usePopover ({ name, isOpen, close, trigger, panel, initialFocus }) {
  const panelId = `${name}-popover-${++counter}`
  // Closed because another popover opened: focus is on its way there, so it
  // must not be pulled back to this trigger.
  const entry = { close: () => close({ restoreFocus: false }) }

  const release = () => {
    if (current === entry) current = null
  }

  watch(isOpen, async (open) => {
    if (!open) {
      release()
      return
    }

    if (current && current !== entry) {
      const previous = current
      current = null
      previous.close()
    }
    current = entry

    await nextTick()
    if (!isOpen.value) return
    const target = (initialFocus && initialFocus()) || tabbablesIn(panel.value)[0] || panel.value
    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true })
  })

  const onKeydown = (event) => {
    if (event.key !== 'Tab' || !isOpen.value) return
    const items = tabbablesIn(panel.value)
    if (!items.length) return

    const active = document.activeElement
    if (event.shiftKey && active === items[0]) {
      event.preventDefault()
      close({ restoreFocus: true })
    } else if (!event.shiftKey && active === items[items.length - 1]) {
      event.preventDefault()
      // Measured before closing, while the panel is still there to be skipped.
      const next = nextTabbableAfter(trigger.value, panel.value)
      close({ restoreFocus: !next })
      if (next) next.focus()
    }
  }

  onBeforeUnmount(release)

  return { panelId, onKeydown }
}
